import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AuditResultQuestion } from 'src/app/core-module/models/Audits/AuditResults/audit-result-question';
import { UpdateEventQuestionRequest } from 'src/app/core-module/models/Events/update-event-question-request';
import { EventsService } from 'src/app/core-module/services/events.service';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { formatLocalDateAnswer, formatLocalDateAnswerDisplay, parseLocalDateAnswer } from 'src/app/core-module/models/Utils/local-date-answer';
import { SelectModule } from 'primeng/select';
import { RadioButtonModule } from 'primeng/radiobutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService } from 'primeng/api';
import { AddEventQuestionAttachmentRequest } from 'src/app/core-module/models/Events/add-event-question-attachment-request';
import { RemoveEventQuestionAttachmentRequest } from 'src/app/core-module/models/Events/remove-event-question-attachment-request';
import { AddEventQuestionPhotoRequest } from 'src/app/core-module/models/Events/add-event-question-photo-request';
import { RemoveEventQuestionPhotoRequest } from 'src/app/core-module/models/Events/remove-event-question-photo-request';
import { environment } from 'src/environments/environment';
import { ComplianceActionService } from 'src/app/core-module/services/compliance-action.service';
import { ActionUserRequest } from 'src/app/core-module/models/Compliance-Actions/action-user-request';
import { BasicUser } from 'src/app/core-module/models/basic-user';
import { ComplianceAction } from 'src/app/core-module/models/Audits/compliance-action';
import { BasicAction } from 'src/app/core-module/models/Audits/AuditResults/basic-action';
import { UserService } from 'src/app/core-module/services/user.service';
import { UtilService } from 'src/app/core-module/services/util.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-event-question-edit',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SelectModule, RadioButtonModule, MultiSelectModule, DatePickerModule, InputTextModule, TextareaModule, ToggleSwitchModule, ConfirmPopupModule],
  templateUrl: './event-question-edit.component.html',
  styleUrl: './event-question-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ConfirmationService]
})
export class EventQuestionEditComponent {

  eventId: number = -1;
  auditResultId: number = -1;
  questionId: number = -1;

  question: AuditResultQuestion = new AuditResultQuestion({
    QuestionHeader: '',
    QuestionBody: '',
    QuestionResult: '',
    QuestionComments: ''
  });

  newAnswer: string = '';
  newComment: string = '';
  naSelected: boolean = false;
  selectedAnswerId: number = -1;
  selectedAnswerIds: number[] = [];
  dateValue: Date | null = null;
  saving: boolean = false;
  errorMessage: string = '';
  addingAttachment: boolean = false;
  removingAttachmentId: number = -1;
  addingPhoto: boolean = false;
  removingPhotoGuid: string = '';
  actionUsers: BasicUser[] = [];
  loadingActionUsers: boolean = false;
  actionAssignee: BasicUser | null = null;
  actionDescription: string = '';
  actionDeadline: Date | null = null;
  actionFormError: string = '';
  actionDescriptionError: string = '';
  actionOwnerError: string = '';
  actionDateError: string = '';
  actionMinDate: Date = this.startOfToday();
  stagedActions: ComplianceAction[] = [];
  savingActions: boolean = false;
  existingActions: BasicAction[] = [];
  loadingActions: boolean = false;
  editingSection: string = '';
  attachmentsExpanded: boolean = false;
  photosExpanded: boolean = false;

  constructor(private route: ActivatedRoute, private router: Router, private eventsService: EventsService, private toast: ToastrService, private confirmationService: ConfirmationService, private complianceActionService: ComplianceActionService, private userHelperService: UserService, private util: UtilService){}

  ngOnInit(){
    this.route.queryParams.subscribe(params => {
      this.eventId = Number(params['eventId'] ?? -1);
      this.auditResultId = Number(params['auditResultId'] ?? -1);
      this.questionId = Number(params['questionId'] ?? -1);

      const navState = history.state?.question;
      if (navState){
        this.question = new AuditResultQuestion(navState);
      }
      this.loadActionUsers();
      this.loadExistingActions();
      this.syncQuestionState();
    });
  }

  private prefillAnswer(){
    if (this.question.QuestionAnswers?.length){
      this.question.QuestionAnswers = [...this.question.QuestionAnswers].sort((a,b) => (a.Seq ?? 0) - (b.Seq ?? 0));
    }
    this.selectedAnswerId = this.question.SelectedAnswerID ?? -1;
    this.selectedAnswerIds = (this.question.SelectedAnswerIDs ?? []).map(x => Number(x));
    const rawResult = this.question.ResultValue || '';
    if ((this.question.QuestionTypeID === 1 || this.question.QuestionTypeID === 2) && this.selectedAnswerId === -1 && rawResult){
      const parsed = Number(rawResult);
      if (!Number.isNaN(parsed)){
        this.selectedAnswerId = parsed;
      }
    }
    if (this.question.QuestionTypeID === 9 || this.question.QuestionTypeID === 10){
      this.dateValue = this.parseDate(rawResult || this.question.QuestionResult);
    } else if (this.question.QuestionTypeID === 4 || this.question.QuestionTypeID === 5 || this.question.QuestionTypeID === 11){
      this.newAnswer = rawResult || this.question.QuestionResult;
    } else if (this.question.QuestionTypeID === 8){
      this.newAnswer = rawResult || this.question.QuestionResult;
    } else if (this.question.QuestionTypeID === 3 && this.selectedAnswerIds.length === 0 && rawResult){
      this.selectedAnswerIds = this.parseAnswerIds(rawResult);
    } else if (!this.newAnswer && rawResult){
      this.newAnswer = rawResult;
    }
  }

  private syncQuestionState(){
    this.newComment = this.question.QuestionComments ?? '';
    this.naSelected = this.question.NASelected;
    this.prefillAnswer();
    this.existingActions = this.question.Actions ?? this.existingActions;
  }

  private parseAnswerIds(value: string): number[]{
    try{
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)){
        return parsed.map(v => Number(v)).filter(v => !Number.isNaN(v));
      }
    }catch{}
    return [];
  }

  private parseDate(value: string): Date | null{
    return parseLocalDateAnswer(value);
  }

  onSelectAnswer(answerId: number){
    this.selectedAnswerId = answerId;
    this.errorMessage = '';
  }

  onMultiChange(answerIds: number[]){
    this.selectedAnswerIds = answerIds ?? [];
    this.errorMessage = '';
  }

  isEditing(section: string): boolean{
    return this.editingSection === section;
  }

  canEdit(section: string): boolean{
    return !this.editingSection || this.editingSection === section;
  }

  startEdit(section: string){
    if (!this.canEdit(section)){
      return;
    }
    this.editingSection = section;
    if (section === 'answer' || section === 'comments'){
      this.resetQuestionInputs();
    }
    if (section === 'actions'){
      this.actionFormError = '';
    }
  }

  cancelEdit(section: string){
    if (this.editingSection !== section){
      return;
    }
    if (section === 'answer' || section === 'comments'){
      this.resetQuestionInputs();
    }
    if (section === 'actions'){
      this.clearStagedActions();
    }
    this.editingSection = '';
    this.errorMessage = '';
  }

  private resetQuestionInputs(){
    this.newComment = this.question.QuestionComments ?? '';
    this.naSelected = this.question.NASelected;
    this.prefillAnswer();
  }

  save(section: 'answer' | 'comments' = 'answer'){
    const validationError = this.validate(section);
    if (validationError){
      this.errorMessage = validationError;
      return;
    }
    const request = new UpdateEventQuestionRequest({
      EventID: this.eventId,
      AuditResultID: this.auditResultId,
      AuditResultDetailID: this.question.AuditResultDetailID,
      QuestionID: this.question.QuestionID,
      QuestionTypeID: this.question.QuestionTypeID,
      NASelected: this.naSelected,
      Comments: this.newComment?.trim() ?? ''
    });
    if (!this.naSelected){
      switch (this.question.QuestionTypeID){
        case 1:
        case 2:
          request.AnswerID = this.selectedAnswerId;
          break;
        case 3:
          request.AnswerIDs = [...this.selectedAnswerIds];
          break;
        case 9:
        case 10:
          request.AnswerText = this.dateValue
            ? formatLocalDateAnswer(this.dateValue, this.question.QuestionTypeID === 10)
            : '';
          break;
        default:
          request.AnswerText = this.newAnswer?.trim() ?? '';
          break;
      }
    }
    this.errorMessage = '';
    this.saving = true;
    this.eventsService.updateEventQuestion(request).subscribe({
      next: (res) => {
        this.saving = false;
        if (res?.IsError){
          this.errorMessage = res.ErrorMessage || 'Unable to update question.';
          return;
        }
        this.editingSection = '';
        this.question.QuestionComments = request.Comments;
        this.question.NASelected = request.NASelected;
        // update answer/result display locally
        if (request.NASelected){
          this.question.QuestionResult = 'N/A';
          this.question.ResultValue = '';
          this.question.SelectedAnswerID = -1;
          this.question.SelectedAnswerIDs = [];
        } else {
          switch (this.question.QuestionTypeID){
            case 1:
            case 2: {
              this.question.SelectedAnswerID = request.AnswerID ?? -1;
              const ans = this.question.QuestionAnswers?.find(a => a.QuestionAnswerID === this.question.SelectedAnswerID);
              this.question.QuestionResult = ans?.AnswerText || '';
              this.question.ResultValue = this.question.SelectedAnswerID?.toString() ?? '';
              break;
            }
            case 3: {
              this.question.SelectedAnswerIDs = request.AnswerIDs ?? [];
              const texts = (this.question.QuestionAnswers || []).filter(a => this.question.SelectedAnswerIDs.includes(a.QuestionAnswerID)).map(a => a.AnswerText);
              this.question.QuestionResult = texts.join(', ');
              this.question.ResultValue = JSON.stringify(this.question.SelectedAnswerIDs);
              break;
            }
            case 9:
            case 10: {
              this.question.ResultValue = request.AnswerText || '';
              this.question.QuestionResult = formatLocalDateAnswerDisplay(
                request.AnswerText,
                this.question.QuestionTypeID === 10
              );
              break;
            }
            default: {
              this.question.ResultValue = request.AnswerText || '';
              this.question.QuestionResult = request.AnswerText || '';
              break;
            }
          }
        }
        this.toast.addToastr(new Toastr({ Type: 2, Message: 'Question updated.' }));
      },
      error: () => {
        this.saving = false;
        this.errorMessage = 'Unable to update question.';
      }
    });
  }

  saveAnswer(){
    if (!this.isEditing('answer')){
      return;
    }
    this.save('answer');
  }

  saveComments(){
    if (!this.isEditing('comments')){
      return;
    }
    this.save('comments');
  }

  cancel(){
    if (this.eventId && this.eventId > 0){
      this.router.navigate(['/events', this.eventId], { queryParams: { tab: 'details' }});
    }else{
      this.router.navigate(['/events']);
    }
  }

  private validate(section: 'answer' | 'comments' = 'answer'): string{
    if (!this.question.AuditResultDetailID || this.question.AuditResultDetailID <= 0){
      return 'Question reference missing.';
    }
    if (section === 'comments'){
      return '';
    }
    if (!this.isEditing('answer')){
      return 'Click Edit to update the answer.';
    }
    if (this.naSelected){
      return '';
    }
    switch (this.question.QuestionTypeID){
      case 1:
      case 2:
        if (this.question.QuestionAnswers && this.question.QuestionAnswers.length > 0){
          if (this.selectedAnswerId === -1){
            return 'Please select an answer.';
          }
          return '';
        }
        return 'No answer options available for this question.';
      case 3:
        if (this.question.QuestionAnswers && this.question.QuestionAnswers.length === 0){
          return 'No answer options available for this question.';
        }
        if (!this.selectedAnswerIds || this.selectedAnswerIds.length === 0){
          return 'Please select at least one answer.';
        }
        return '';
      case 9:
      case 10:
        if (!this.dateValue){
          return 'Please provide a date.';
        }
        return '';
      default:
        if (!this.newAnswer || this.newAnswer.trim().length === 0){
          return 'Please enter an answer.';
        }
        return '';
    }
  }

  photoSrc(guid: string): string{
    return `${environment.apiUrl}audits/getphoto/${guid}`;
  }

  downloadAttachment(guid: string){
    window.open(`${environment.apiUrl}audits/getattachment/${guid}`,'_blank');
  }

  onAttachmentSelected(event: any){
    const file: File = event?.target?.files?.[0];
    if (!file || this.question.AuditResultDetailID <= 0){
      return;
    }
    this.addingAttachment = true;
    this.fileToBase64(file).then(content => {
      const request = new AddEventQuestionAttachmentRequest({
        EventID: this.eventId,
        AuditResultID: this.auditResultId,
        AuditResultDetailID: this.question.AuditResultDetailID,
        QuestionID: this.question.QuestionID,
        FileName: file.name,
        MimeType: file.type || 'application/octet-stream',
        FileContent: content
      });
      this.eventsService.addEventQuestionAttachment(request).subscribe({
        next: res => {
          this.addingAttachment = false;
          if (res?.IsError){
            this.errorMessage = res.ErrorMessage || 'Unable to add attachment.';
            return;
          }
          this.question.QuestionAttachments = this.question.QuestionAttachments || [];
          this.question.QuestionAttachments.push({
            AttachmentID: res.AttachmentID,
            FileName: res.FileName,
            GUID: res.GUID
          } as any);
        },
        error: () => {
          this.addingAttachment = false;
          this.errorMessage = 'Unable to add attachment.';
        }
      });
    }).catch(() => {
      this.addingAttachment = false;
      this.errorMessage = 'Unable to read file.';
    });
  }

  removeAttachment(att: any){
    if (!att){
      return;
    }
    this.removingAttachmentId = att.AttachmentID;
    const request = new RemoveEventQuestionAttachmentRequest({
      EventID: this.eventId,
      AuditResultID: this.auditResultId,
      AuditResultDetailID: this.question.AuditResultDetailID,
      AttachmentID: att.AttachmentID,
      AttachmentGUID: att.GUID
    });
    this.eventsService.removeEventQuestionAttachment(request).subscribe({
      next: res => {
        this.removingAttachmentId = -1;
        if (res?.IsError){
          this.errorMessage = res.ErrorMessage || 'Unable to remove attachment.';
          return;
        }
        this.question.QuestionAttachments = (this.question.QuestionAttachments || []).filter(a => a.AttachmentID !== att.AttachmentID);
      },
      error: () => {
        this.removingAttachmentId = -1;
        this.errorMessage = 'Unable to remove attachment.';
      }
    });
  }

  onPhotoSelected(event: any){
    const file: File = event?.target?.files?.[0];
    if (!file || this.question.AuditResultDetailID <= 0){
      return;
    }
    this.addingPhoto = true;
    this.fileToBase64(file).then(content => {
      const request = new AddEventQuestionPhotoRequest({
        EventID: this.eventId,
        AuditResultID: this.auditResultId,
        AuditResultDetailID: this.question.AuditResultDetailID,
        QuestionID: this.question.QuestionID,
        FileName: file.name,
        MimeType: file.type || 'image/jpeg',
        PhotoContent: content
      });
      this.eventsService.addEventQuestionPhoto(request).subscribe({
        next: res => {
          this.addingPhoto = false;
          if (res?.IsError){
            this.errorMessage = res.ErrorMessage || 'Unable to add photo.';
            return;
          }
          this.question.Photos = this.question.Photos || [];
          this.question.Photos.push(res.GUID);
        },
        error: () => {
          this.addingPhoto = false;
          this.errorMessage = 'Unable to add photo.';
        }
      });
    }).catch(() => {
      this.addingPhoto = false;
      this.errorMessage = 'Unable to read photo.';
    });
  }

  removePhoto(guid: string){
    if (!guid){
      return;
    }
    this.removingPhotoGuid = guid;
    const request = new RemoveEventQuestionPhotoRequest({
      EventID: this.eventId,
      AuditResultID: this.auditResultId,
      AuditResultDetailID: this.question.AuditResultDetailID,
      PhotoGUID: guid
    });
    this.eventsService.removeEventQuestionPhoto(request).subscribe({
      next: res => {
        this.removingPhotoGuid = '';
        if (res?.IsError){
          this.errorMessage = res.ErrorMessage || 'Unable to remove photo.';
          return;
        }
        this.question.Photos = (this.question.Photos || []).filter(p => p !== guid);
      },
      error: () => {
        this.removingPhotoGuid = '';
        this.errorMessage = 'Unable to remove photo.';
      }
    });
  }

  confirmRemoveAttachment(event: Event, att: any){
    const target = event.currentTarget as HTMLElement | null;
    if (!target){
      return;
    }
    this.confirmationService.confirm({
      target,
      key: 'confirmPopup',
      header: 'Remove attachment',
      message: 'Are you sure you want to remove this attachment?',
      defaultFocus: 'reject',
      accept: () => this.removeAttachment(att),
      reject: () => {}
    });
  }

  confirmRemovePhoto(event: Event, guid: string){
    const target = event.currentTarget as HTMLElement | null;
    if (!target){
      return;
    }
    this.confirmationService.confirm({
      target,
      key: 'confirmPopup',
      header: 'Remove photo',
      message: 'Are you sure you want to remove this photo?',
      defaultFocus: 'reject',
      accept: () => this.removePhoto(guid),
      reject: () => {}
    });
  }

  private fileToBase64(file: File): Promise<string>{
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const res = (reader.result as string) || '';
        const base64 = res.includes(',') ? res.split(',')[1] : res;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  onConfirmAccept(confirmation: any){
    confirmation?.acceptEvent?.emit();
    this.confirmationService.close();
  }

  onConfirmReject(confirmation: any){
    confirmation?.rejectEvent?.emit();
    this.confirmationService.close();
  }

  private loadActionUsers(){
    if (this.eventId <= 0){
      return;
    }
    this.loadingActionUsers = true;
    const req = new ActionUserRequest({ EventID: this.eventId });
    this.complianceActionService.getActionUsers(req).subscribe({
      next: users => {
        this.loadingActionUsers = false;
        this.actionUsers = this.userHelperService.sortUsers(users || []);
        if (!this.actionUsers.some(u => u.UserID === -1)){
          const placeholder = new BasicUser({ UserID: -1, DisplayName: 'Please Select...', Seq: 0, UserTypeID: 0 }) as any;
          placeholder.Disabled = true;
          this.actionUsers.unshift(placeholder);
        }
        this.actionAssignee = this.actionUsers.find(u => u.UserID === -1) || null;
      },
      error: () => {
        this.loadingActionUsers = false;
        this.actionUsers = [];
      }
    });
  }

  private loadExistingActions(){
    if (this.eventId <= 0 || !this.question?.AuditResultDetailID){
      this.existingActions = this.question?.Actions ?? [];
      return;
    }
    this.loadingActions = true;
    this.complianceActionService.getEventActions(this.eventId, false, -1, 1, this.question.AuditResultDetailID).subscribe({
      next: actions => {
        this.loadingActions = false;
        this.existingActions = actions || [];
      },
      error: () => {
        this.loadingActions = false;
        this.existingActions = this.question?.Actions ?? [];
      }
    });
  }

  addStagedAction(){
    if (!this.isEditing('actions')){
      return;
    }
    this.actionFormError = '';
    this.actionDescriptionError = '';
    this.actionOwnerError = '';
    this.actionDateError = '';
    if (!this.actionDescription || this.actionDescription.trim().length === 0){
      this.actionFormError = 'Please enter an action description.';
      this.actionDescriptionError = 'Description is required.';
    }
    if (!this.actionAssignee || this.actionAssignee.UserID === -1){
      this.actionFormError = this.actionFormError || 'Please select an action owner.';
      this.actionOwnerError = 'Please select an action owner.';
    }
    if (!this.actionDeadline){
      this.actionFormError = this.actionFormError || 'Please select a due date.';
      this.actionDateError = 'Please select a due date.';
    }else{
      const today = new Date(); today.setHours(0,0,0,0);
      const due = new Date(this.actionDeadline); due.setHours(0,0,0,0);
      if (due < today){
        this.actionFormError = this.actionFormError || 'Due date cannot be in the past.';
        this.actionDateError = 'Due date cannot be in the past.';
      }
    }
    if (this.actionFormError){
      return;
    }
    const cleanedDeadline = this.util.removeTimezoneFromDate(this.actionDeadline!);
    const action = new ComplianceAction({
      ActionNote: this.actionDescription.trim(),
      ActionOwner: this.actionAssignee,
      ActionOwnerTypeID: this.actionAssignee!.UserTypeID,
      ActionDeadline: cleanedDeadline,
      EventID: this.eventId,
      ActionTypeID: 1,
      AuditResultDetailID: this.question.AuditResultDetailID
    });
    this.stagedActions.push(action);
    this.actionDescription = '';
    this.actionDeadline = null;
    this.actionAssignee = this.actionUsers.find(u => u.UserID === -1) || null;
  }

  removeStagedAction(index: number){
    if (!this.isEditing('actions')){
      return;
    }
    if (index < 0 || index >= this.stagedActions.length){
      return;
    }
    this.stagedActions.splice(index, 1);
  }

  saveStagedActions(){
    if (!this.isEditing('actions')){
      return;
    }
    if (!this.stagedActions || this.stagedActions.length === 0){
      return;
    }
    this.savingActions = true;
    const requests = this.stagedActions.map(action => {
      action.EventID = this.eventId;
      action.ActionTypeID = 1;
      action.AuditResultDetailID = this.question.AuditResultDetailID;
      action.ActionDeadline = this.util.removeTimezoneFromDate(action.ActionDeadline);
      return this.complianceActionService.saveEventAction(action);
    });
    forkJoin(requests).subscribe({
      next: results => {
        this.savingActions = false;
        const hasError = (results || []).some(r => r?.IsError);
        if (hasError){
          this.toast.addToastr(new Toastr({ Type: 3, Message: 'One or more actions could not be saved.' }));
          return;
        }
        this.toast.addToastr(new Toastr({ Type: 2, Message: 'Actions created.' }));
        this.clearStagedActions();
        this.editingSection = '';
        this.loadExistingActions();
      },
      error: () => {
        this.savingActions = false;
        this.toast.addToastr(new Toastr({ Type: 3, Message: 'Unable to save actions.' }));
      }
    });
  }

  private clearStagedActions(){
    this.stagedActions = [];
    this.actionFormError = '';
    this.actionDescription = '';
    this.actionDeadline = null;
    this.actionAssignee = this.actionUsers.find(u => u.UserID === -1) || null;
  }

  toggleAttachments(){
    this.attachmentsExpanded = !this.attachmentsExpanded;
  }

  togglePhotos(){
    this.photosExpanded = !this.photosExpanded;
  }

  openExistingAction(id: number){
    if (!id || id === -1){
      return;
    }
    const returnUrl = this.router.url;
    this.router.navigate(['/actionv2'], {
      queryParams: { id, from: 'event-question-edit', eventId: this.eventId, auditResultId: this.auditResultId, questionId: this.questionId, returnUrl },
      state: { question: this.question }
    });
  }

  isImageAttachment(att: any): boolean{
    const mime = (att?.MimeType || '').toLowerCase();
    if (mime.startsWith('image/')){
      return true;
    }
    const name = (att?.FileName || '').toLowerCase();
    return /\.(png|jpe?g|gif|bmp|webp|svg)$/.test(name);
  }

  attachmentSrc(guid: string): string{
    return `${environment.apiUrl}audits/getattachment/${guid}`;
  }

  private startOfToday(): Date{
    const d = new Date();
    d.setHours(0,0,0,0);
    return d;
  }
}
